import { Injectable, NgZone } from '@angular/core';
import { Subject, fromEvent, merge, Subscription, timer, lastValueFrom } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { LookupService } from '../lookup/lookup.service';
import { LookupTypeEnum } from '../../models/lookup-type.enum';
import { hasAuthTokens } from '../../util/auth-tokens.util';

@Injectable({
  providedIn: 'root'
})
export class SessionInactivityService {
  // Emits when user becomes inactive
  private readonly inactivity$ = new Subject<void>();

  // Signals teardown for all subscriptions
  private destroy$: Subject<void> = new Subject<void>();

  // Timer subscription for inactivity
  private timerSub: Subscription | null = null;

  // User activity event subscription
  private activityEventsSub: Subscription | null = null;

  private isInitialized = false;
  private speechInProgress = false;
  private inactivityTimeoutSec = 300; // default fallback

  constructor(private readonly zone: NgZone, private readonly lookupService: LookupService) { }

  /** Initialize on login or reload */
  async initOnLogin() {
    if (this.isInitialized) {
      return; // already set up
    }

    if (!hasAuthTokens()) {
      return;
    }

    this.destroy$ = new Subject<void>();
    this.isInitialized = true;

    // Fetch the inactivity config every time user logs in or reloads
    const config = await lastValueFrom(
      this.lookupService.getLookupValuesByType(LookupTypeEnum.SESSION_TIMEOUT_IN_MIN)
    );
    const sessionTimeoutMinutes = config[0]?.lookupValue;
    this.inactivityTimeoutSec = Number(sessionTimeoutMinutes) * 60;


    this.listenToUserActivity();
    this.restoreOrStartTimer();
  }

  onReInit() {
    this.isInitialized = true;
    //set a new time
    sessionStorage.setItem('inactivityStart', Date.now().toString());
    this.listenToUserActivity();
    this.restoreOrStartTimer();
  }

  stopOnLogout() {
    this.unsubscribeEvents();
    sessionStorage.removeItem('inactivityStart');
    this.destroy$.next();
  }

  unsubscribeEvents() {
    this.isInitialized = false;
    this.timerSub?.unsubscribe();
    this.activityEventsSub?.unsubscribe();
    this.activityEventsSub = null;
  }

  setSpeechInProgress(active: boolean) {
    this.speechInProgress = active;
    if (!active) {
      //on stopping the speech, reset the timer
      this.restartTimer();
    }
  }

  /** Event hook for inactivity */
  onInactive(callback: () => void) {
    this.inactivity$
      .pipe(takeUntil(this.destroy$))
      .subscribe(callback);
  }

  restartTimer() {
    sessionStorage.setItem('inactivityStart', Date.now().toString());
    this.startInactivityTimer();
  }

  private listenToUserActivity() {
    if (this.activityEventsSub) return;

    this.zone.runOutsideAngular(() => {
      const mouse$ = fromEvent(document, 'click');
      const key$ = fromEvent(document, 'keydown');
      const touch$ = fromEvent(document, 'touchstart');

      const activity$ = merge(mouse$, key$, touch$).pipe(
        filter(() => this.isInitialized)
      );

      this.activityEventsSub = activity$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.restartTimer());
    });
  }

  private restoreOrStartTimer() {
    const stored = sessionStorage.getItem('inactivityStart');
    const now = Date.now();

    if (stored) {
      const elapsedMs = now - parseInt(stored, 10);
      const remaining = this.inactivityTimeoutSec * 1000 - elapsedMs;

      if (remaining <= 0) {
        this.triggerInactivity();
        return;
      }

      this.startInactivityTimer(remaining);
    } else {
      this.restartTimer(); // starts fresh
    }
  }

  private startInactivityTimer(durationMs?: number) {
    this.timerSub?.unsubscribe();

    const timeout = durationMs ?? this.inactivityTimeoutSec * 1000;

    this.timerSub = timer(timeout)
      .pipe(
        takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.speechInProgress && this.isInitialized) {
          this.triggerInactivity();
        }
      });
  }

  private triggerInactivity() {

    this.inactivity$.next();
    this.unsubscribeEvents();
  }
}
